import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card"; // Assuming shadcn/ui Card
import {
  ThumbsUp,
  ThumbsDown,
  Building,
  MapPin,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertCircle,
  ExternalLink,
  Info,
} from "lucide-react";
import { RenderHtml } from "@/components/RenderHtml";
import { JobPost } from "./types/job-post";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { WageFrequency } from "@api/shared-runtime/enums";
import { wageFrequencyToEnding } from "@/features/job-offer/utils/wage-frequency-to-ending";
import { formatLocation } from "./utils/format-location";
import { htmlToText } from "./utils/html-to-text";
import { abbreviateProfessionalType } from "./utils/abbreviate-professional-type";
import { formatFacilityWithType } from "./utils/format-facility-with-type";
import { JobShareButton } from "./components/job-share-button";
import { ExternalJobBadge } from "./components/external-job-badge";
import { EligibilityBadge } from "./components/eligibility-badge";
import { getJobDurationLabel } from "@/common/enums/JobDuration";
import { JobPostExternalAcceptDialog } from "./job-post-external-accept-dialog";

const formatCurrency = (value: number | null) => {
  if (value === null || value === undefined || value === 0) {
    return "";
  } // Handle null/undefined/zero values

  // Large yearly salaries are abbreviated to K/M so the rate stays on one line
  // in the card header.
  const numberFormatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  };

  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    // Format as M, keep one decimal place
    return `$${Intl.NumberFormat("en-US", numberFormatOptions).format(millions)}M`;
  } else if (value >= 5_000) {
    const thousands = value / 1_000;
    // Format as K, don't round
    return `$${Intl.NumberFormat("en-US", numberFormatOptions).format(thousands)}K`;
  }
  // Format regular number with commas
  return `$${new Intl.NumberFormat("en-US", numberFormatOptions).format(value)}`;
};

export function JobCard({
  job,
  onInterestChange,
  className,
}: {
  job: JobPost;
  onInterestChange: (
    jobId: string,
    isInterested: boolean | null,
  ) => Promise<void>;
  className?: string;
}) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [interestLoading, setInterestLoading] = useState<boolean | null>(null);
  const [showExternalDialog, setShowExternalDialog] = useState(false);

  const facilityWithType = formatFacilityWithType({
    facility: job.facility,
    practiceSetting: job.practiceSetting,
  });
  const location = formatLocation(job);
  // For remote jobs with no named facility the "facility" line is just the
  // practice setting (e.g. "Remote/Virtual"), which repeats the location.
  const facilityRepeatsRemote =
    !!job.remote && !job.facility && /remote|virtual/i.test(facilityWithType);

  // --- Event Handlers ---
  const handleInterest = async (isInterested: boolean) => {
    let newStatus: boolean | null = isInterested;
    if (job.decision?.interested === isInterested) {
      newStatus = null;
    }
    setInterestLoading(newStatus);
    try {
      await onInterestChange(job.id, newStatus);
    } finally {
      setInterestLoading(null);
    }
  };

  // --- Memoized Values (Uses updated formatCurrency) ---
  const salaryRange = useMemo(() => {
    const ending = job.wageFrequencyId
      ? wageFrequencyToEnding[job.wageFrequencyId as WageFrequency]
      : "";
    const salaryMinNumber = Number(job.wageMinAmount);
    const salaryMaxNumber = Number(job.wageMaxAmount);
    if (
      salaryMinNumber &&
      salaryMaxNumber &&
      salaryMinNumber !== salaryMaxNumber
    ) {
      return `${formatCurrency(salaryMinNumber)}-${formatCurrency(salaryMaxNumber)}${ending}`;
    } else if (salaryMinNumber) {
      return `${formatCurrency(salaryMinNumber)}${ending}`;
    } else if (salaryMaxNumber) {
      return `${formatCurrency(salaryMaxNumber)}${ending}`;
    }
    return "";
  }, [job.wageMinAmount, job.wageMaxAmount, job.wageFrequencyId]);

  const isGuaranteedBaseHourly =
    job.guaranteedBasePay && job.wageFrequencyId === WageFrequency.Hourly;

  const specialtyNames = job.specialties?.map((s) => s.name).join(", ");
  const professionalTypeAbbrevs = job.healthcareProfessionalTypes
    ?.map((type) => abbreviateProfessionalType(type))
    .join("/");

  const durationBadgeIds = job.durationIds ?? [];

  const pressedNotInterested = job.decision?.interested === false;
  const pressedInterested = job.decision?.interested === true;

  const scheduleText = useMemo(
    () => (job.scheduleDescription ? htmlToText(job.scheduleDescription) : ""),
    [job.scheduleDescription],
  );

  // --- Render Logic ---
  return (
    <Card
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm",
        className,
      )}
    >
      {/* Header */}
      <div className="p-5 sm:p-6 space-y-3">
        <h3 className="text-lg sm:text-xl font-semibold text-slate-900 leading-snug">
          {job.title || "Job Title Not Available"}
        </h3>

        {(specialtyNames || professionalTypeAbbrevs) && (
          <p className="text-sm text-slate-600">
            {specialtyNames && (
              <span className="font-medium text-slate-800">
                {specialtyNames}
              </span>
            )}
            {specialtyNames && professionalTypeAbbrevs && (
              <span className="text-slate-400"> · </span>
            )}
            {professionalTypeAbbrevs}
          </p>
        )}

        {salaryRange && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold text-primary">
              {salaryRange}
            </span>
            {isGuaranteedBaseHourly &&
              (job.guaranteedBasePayDescription ? (
                <Popover>
                  <PopoverTrigger>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      <Info className="h-3 w-3" />
                      Guaranteed base
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 sm:w-80 p-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">
                        Guaranteed Base Details
                      </h4>
                      <p className="text-sm text-slate-700">
                        {job.guaranteedBasePayDescription}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="inline-flex items-center text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  Guaranteed base
                </span>
              ))}
          </div>
        )}

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
          {location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-slate-400" />
              <span>{location}</span>
            </div>
          )}
          {facilityWithType && !facilityRepeatsRemote && (
            <div className="flex items-center gap-1.5">
              <Building className="h-4 w-4 text-slate-400" />
              <span>{facilityWithType}</span>
            </div>
          )}
          {scheduleText && (
            <div className="flex items-start gap-1.5 basis-full">
              <Clock className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span className="line-clamp-2">{scheduleText}</span>
            </div>
          )}
        </div>

        {/* Badge row */}
        {(durationBadgeIds.length > 0 ||
          !!job.externalUrl ||
          job.eligibility?.blocked) && (
          <div className="flex flex-wrap gap-2">
            {durationBadgeIds.map((durationId) => (
              <span
                key={durationId}
                className="bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium rounded-md px-2 py-0.5"
              >
                {getJobDurationLabel(durationId)}
              </span>
            ))}
            {job.externalUrl && <ExternalJobBadge />}
            <EligibilityBadge eligibility={job.eligibility} />
          </div>
        )}
      </div>

      {/* Action footer */}
      <div className="border-t border-slate-100 px-5 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <Button
          variant="outline"
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          aria-expanded={isAccordionOpen}
          className="gap-2 px-3"
        >
          {isAccordionOpen ? "Hide details" : "View details"}
          {isAccordionOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>

        <div className="flex flex-wrap items-center gap-2">
          <JobShareButton
            jobId={job.id}
            jobTitle={job.title}
            facilityWithType={facilityWithType}
            location={location}
            className="h-12 w-12 shrink-0 px-0"
          />

          <Button
            variant="outline"
            disabled={interestLoading === false}
            aria-label="Hide job"
            aria-pressed={pressedNotInterested}
            onClick={() => handleInterest(false)}
            className={`gap-2 px-3 ${
              pressedNotInterested
                ? "bg-slate-800 border-slate-800 text-white hover:bg-slate-700 hover:text-white"
                : ""
            }`}
          >
            <ThumbsDown
              className={`h-4 w-4 ${pressedNotInterested ? "fill-current" : ""}`}
            />
            <span className="hidden sm:inline">
              {pressedNotInterested ? "Hidden" : "Hide"}
            </span>
          </Button>

          <Button
            disabled={interestLoading === true}
            onClick={() => handleInterest(true)}
            aria-pressed={pressedInterested}
            className={`gap-2 border px-3 ${
              pressedInterested
                ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                : "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
            }`}
          >
            <ThumbsUp
              className={`h-4 w-4 ${pressedInterested ? "fill-current" : ""}`}
            />
            {pressedInterested ? "Applied" : "Apply"}
          </Button>

          {job.externalUrl && pressedInterested && (
            <Button
              onClick={() => setShowExternalDialog(true)}
              className="gap-2 px-3"
            >
              <ExternalLink className="h-4 w-4" />
              View Application
            </Button>
          )}
        </div>
      </div>

      {/* Expandable Details Section */}
      {isAccordionOpen && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-5 sm:px-6 py-5 space-y-5">
          {job.scheduleDescription ? (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Schedule
              </h3>
              <RenderHtml html={job.scheduleDescription} />
            </div>
          ) : null}

          {job.responsibilities && job.responsibilities.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Responsibilities
              </h3>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                {job.responsibilities.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {job.requiredQualifications &&
          job.requiredQualifications.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Required Qualifications
              </h3>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                {job.requiredQualifications.map(
                  (item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
            </div>
          ) : null}

          {job.preferredQualifications &&
          job.preferredQualifications.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Preferred Qualifications
              </h3>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                {job.preferredQualifications.map(
                  (item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
            </div>
          ) : null}

          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Job Description
            </h3>
            {job.description ? (
              <div className="text-sm text-slate-700">
                <RenderHtml html={job.description} />
              </div>
            ) : (
              <p className="text-sm text-slate-700 flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3">
                <AlertCircle
                  className="h-4 w-4 text-slate-400 shrink-0"
                  aria-label="Missing Information"
                />
                <span>Job description not provided</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* External Job Dialog */}
      {showExternalDialog && job.externalUrl && (
        <JobPostExternalAcceptDialog
          jobId={job.id}
          eligibility={job.eligibility}
          externalUrl={job.externalUrl}
          onClose={() => setShowExternalDialog(false)}
          onSuccess={() => {}}
          alreadyLiked={true}
        />
      )}
    </Card>
  );
}
